/**
 * Per-route Suspense fallbacks.
 *
 * A single generic skeleton for every route is a layout jump waiting to happen:
 * the board is columns, analytics is a card grid, settings is a rail plus a
 * measure-capped page. Each fallback below reproduces the *chrome* of the screen
 * it stands in for — toolbar heights, rail width, column track width, card
 * geometry — so the swap to the real screen moves nothing.
 *
 * These live in the eager shell bundle, so they deliberately import nothing from
 * `features/` — pulling a lazy chunk's module in here would undo the code-split.
 */

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { AVATAR_PX, Skeleton } from '@/components/ui';
import {
  CARD_GAP,
  CARD_HEIGHT,
  CARD_SLOT,
  COLUMN_HEADER_HEIGHT,
  COLUMN_INSET,
  RAIL_WIDTH,
  SKELETON_COLUMNS,
  SUBJECT_HEIGHT,
  SUBJECT_LINES,
  TRACK_INSET,
  fitTrack,
  seedColumnWidth,
} from '@/lib/boardGeometry';
import { cn } from '@/lib/cn';
import {
  TASK_BAND_GRID,
  TASK_BAND_H,
  TASK_COLUMNS_ALL,
  TASK_HEAD_H,
  TASK_ROW_HEIGHT,
  TASK_SUBJECT,
  taskGrid,
  type Density,
  type TaskColumnVisibility,
} from '@/lib/constants';

/* ────────────────────────────────────────────────────────────────────────────
   Board

   The geometry is IMPORTED, not transcribed. This fallback and the board itself
   cannot import each other — one is in the eager shell bundle, the other is a
   lazy chunk — so they used to agree by hand and had stopped: this file drew SIX
   260px columns on a 12px gutter with 52px heads and 104px cards, while the
   board draws seven fitted tracks on an 8px gutter with 36px heads and 86px
   cards. Sampled from a navigation commit, that mismatch was ~300ms of a track
   the board was never going to keep, followed by every column moving up to 168px
   sideways. `@/lib/boardGeometry` is the one declaration both sides read; it is
   ~40 lines of constants, so it costs the eager bundle nothing.
   ──────────────────────────────────────────────────────────────────────────── */

export function BoardScreenSkeleton({
  columns = SKELETON_COLUMNS,
  cards,
}: {
  columns?: number;
  cards?: number;
}) {
  /*
    The same fit, seeded the same way, gated the same way. `seedColumnWidth`
    reads the shell region synchronously during render so the first paint is
    already right; the layout effect below measures the true box before the
    browser paints; and the track is `visibility: hidden` until it has, so a
    mis-fitted skeleton is unpaintable rather than merely unlikely.
  */
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [fit, setFit] = useState(() => ({
    width: seedColumnWidth(columns),
    rail: false,
    measured: false,
  }));

  useLayoutEffect(() => {
    const measure = () => {
      const shell = shellRef.current;
      if (!shell || shell.clientWidth === 0) return;
      const next = fitTrack(shell.clientWidth, columns);
      setFit((previous) =>
        previous.measured && previous.width === next.width && previous.rail === next.rail
          ? previous
          : { width: next.width, rail: next.rail, measured: true },
      );
    };
    measure();
    const shell = shellRef.current;
    if (!shell || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(shell);
    return () => observer.disconnect();
  }, [columns]);

  /* How many card boxes reach the fold, derived rather than assumed: the loaded
     column shows the partial card at the bottom edge, so a fixed 5 ended the
     placeholder ~130px above the thing it stands in for. */
  const [fitCards, setFitCards] = useState(6);
  const bodyRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const content = node.clientHeight - COLUMN_INSET * 2;
    if (content <= 0) return;
    setFitCards(Math.max(1, Math.ceil((content + CARD_GAP) / CARD_SLOT)));
  }, []);
  const cardCount = cards ?? fitCards;

  return (
    <div className="flex h-full flex-col" aria-busy="true" aria-label="Loading board">
      {/* `ListToolbar`'s own box: h-10, px-3, gap-1.5 — not h-11/px-4/gap-2,
          which put the whole track 4px low and every control 4px right of where
          the real toolbar puts it. */}
      <div className="flex h-10 shrink-0 items-center gap-1.5 border-b border-line-subtle px-3">
        <Skeleton w={168} h={26} rounded="sm" />
        <Skeleton w={236} h={26} rounded="sm" />
        <Skeleton w={72} h={26} rounded="sm" />
        <Skeleton w={148} h={26} rounded="sm" />
        <Skeleton w={104} h={26} rounded="sm" />
        <span className="flex-1" />
        <Skeleton w={78} h={12} rounded="xs" />
        <Skeleton w={72} h={26} rounded="sm" />
        <Skeleton w={112} h={26} rounded="sm" />
        <Skeleton w={26} h={26} rounded="sm" />
      </div>

      <div
        ref={shellRef}
        className="relative min-h-0 min-w-0 flex-1"
        style={fit.measured ? undefined : { visibility: 'hidden' }}
      >
        <div
          className="absolute inset-y-0 left-0 flex items-stretch gap-2 overflow-hidden py-3"
          style={{
            paddingLeft: TRACK_INSET,
            paddingRight: fit.rail ? 0 : TRACK_INSET,
            right: fit.rail ? RAIL_WIDTH : 0,
          }}
        >
          {Array.from({ length: columns }, (_, column) => (
            <section
              key={column}
              aria-hidden
              style={{ width: fit.width }}
              className="flex h-full shrink-0 flex-col rounded-[var(--radius-lg)] border border-line-container bg-surface-2"
            >
              <div
                style={{ height: COLUMN_HEADER_HEIGHT }}
                className="flex shrink-0 items-center gap-2 border-b border-line-subtle px-2"
              >
                <Skeleton w={14} h={14} rounded="xs" />
                <Skeleton w={68} h={11} rounded="xs" />
                <span className="flex-1" />
                <Skeleton w={22} h={11} rounded="xs" />
              </div>

              <div
                ref={column === 0 ? bodyRef : undefined}
                className="flex min-h-0 flex-1 flex-col overflow-hidden p-2"
                style={{ gap: CARD_GAP }}
              >
                {Array.from({ length: cardCount }, (_, card) => (
                  <div
                    key={card}
                    style={{
                      height: CARD_HEIGHT,
                      opacity: Math.max(0.25, 1 - card * 0.12),
                    }}
                    className="flex shrink-0 flex-col gap-1 rounded-[var(--radius-md)] border border-line-object bg-[var(--surface-card)] p-2"
                  >
                    {/* Subject then ONE meta row — the card's own two boxes. */}
                    <div
                      className="flex shrink-0 flex-col justify-between pr-5"
                      style={{ height: SUBJECT_HEIGHT }}
                    >
                      {Array.from({ length: SUBJECT_LINES }, (_, line) => (
                        <Skeleton
                          key={line}
                          w={line === SUBJECT_LINES - 1 ? '62%' : '100%'}
                          h={10}
                          rounded="xs"
                        />
                      ))}
                    </div>
                    <div className="mt-auto flex h-4 shrink-0 items-center gap-1.5">
                      <Skeleton w={46} h={10} rounded="xs" />
                      <Skeleton w={54} h={14} rounded="xs" />
                      <Skeleton w={62} h={10} rounded="xs" />
                      <span className="flex-1" />
                      <Skeleton w={18} h={12} rounded="xs" />
                      <Skeleton w={16} h={16} rounded="full" />
                    </div>
                  </div>
                ))}
              </div>

              {/* The loaded column's own footer band, so the bottom hairline is
                  in the same place before and after the data lands. */}
              <div className="shrink-0 border-t border-line-subtle p-2">
                <Skeleton w="100%" h="var(--h-control-sm)" rounded="sm" />
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Analytics
   ──────────────────────────────────────────────────────────────────────────── */

/** One chart card: the header line plus a plot area of the real card's height. */
function ChartCardSkeleton({ height, span }: { height: number; span: string }) {
  return (
    <div
      aria-hidden
      className={`${span} flex flex-col rounded-[var(--radius-lg)] border border-line bg-surface-1`}
    >
      <div className="flex items-center gap-3 px-[var(--space-4)] py-[var(--space-3)]">
        <Skeleton w={132} h={12} rounded="xs" />
        <span className="flex-1" />
        <Skeleton w={88} h={22} rounded="sm" />
      </div>
      <div className="border-t border-line-subtle p-[var(--space-4)]">
        <Skeleton w="100%" h={height} rounded="sm" />
      </div>
    </div>
  );
}

export function AnalyticsScreenSkeleton() {
  return (
    <div className="h-full min-h-0 overflow-hidden" aria-busy="true" aria-label="Loading analytics">
      <div className="border-b border-line bg-surface-1">
        <div className="flex h-[var(--h-topbar)] items-center gap-[var(--space-3)] px-[var(--space-6)]">
          <Skeleton w={236} h={30} rounded="sm" />
          <Skeleton w={92} h={12} rounded="xs" />
          <Skeleton w={36} h={18} rounded="full" />
          <Skeleton w={200} h={12} rounded="xs" />
          <span className="flex-1" />
          <Skeleton w={88} h={10} rounded="xs" />
          <Skeleton w={96} h={26} rounded="sm" />
        </div>
        <div className="flex items-center gap-[var(--space-2)] px-[var(--space-6)] pb-[var(--space-3)]">
          <Skeleton w={64} h={12} rounded="xs" />
          <Skeleton w={280} h={12} rounded="xs" />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-[var(--space-4)] px-[var(--space-6)] py-[var(--space-5)]">
        {/* Eight stat tiles, four across — the real metric row is two full rows. */}
        <div className="col-span-12 mobile:col-span-2 grid grid-cols-4 gap-[var(--space-4)] mobile:grid-cols-2">
          {Array.from({ length: 8 }, (_, tile) => (
            <div
              key={tile}
              aria-hidden
              className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-line bg-surface-1 p-[var(--space-4)]"
            >
              <Skeleton w={132} h={9} rounded="xs" />
              <div className="flex items-center gap-2">
                <Skeleton w={78} h={28} rounded="sm" />
                <Skeleton w={54} h={16} rounded="full" />
              </div>
              <Skeleton w={148} h={9} rounded="xs" />
              <Skeleton w="100%" h={34} rounded="xs" />
            </div>
          ))}
        </div>

        <ChartCardSkeleton span="col-span-12" height={300} />
        <ChartCardSkeleton span="col-span-4" height={224} />
        <ChartCardSkeleton span="col-span-8" height={224} />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Settings
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * The settings *pane* only — used for the nested leaf routes, where the rail is
 * already painted by the (already-loaded) layout chunk.
 *
 * `measure` must match the one the real pane passes to `SettingsPage`, or the
 * content visibly narrows or widens when the chunk lands.
 */
export function SettingsPaneSkeleton({
  measure = 'prose',
}: {
  measure?: 'prose' | 'wide';
}) {
  return (
    <div
      className={`w-full px-8 pb-16 pt-8 ${
        measure === 'wide' ? 'max-w-[1144px]' : 'max-w-[784px]'
      }`}
      aria-busy="true"
      aria-label="Loading settings"
    >
      <div className="flex flex-col gap-2 border-b border-line pb-5">
        <Skeleton w={168} h={20} rounded="sm" />
        <Skeleton w={420} h={12} rounded="xs" />
      </div>

      <div className="flex flex-col gap-10 pt-8">
        {Array.from({ length: 2 }, (_, section) => (
          <section key={section} aria-hidden className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Skeleton w={132} h={13} rounded="xs" />
              <Skeleton w={344} h={11} rounded="xs" />
            </div>
            <div className="overflow-hidden rounded-[var(--radius-lg)] border border-line bg-[var(--surface-2)]">
              {Array.from({ length: 3 }, (_, row) => (
                <div
                  key={row}
                  className="grid grid-cols-[minmax(0,1fr)_248px] items-start gap-6 border-b border-line-subtle px-4 py-3.5 last:border-b-0"
                >
                  <div className="flex flex-col gap-1.5">
                    <Skeleton w={148} h={11} rounded="xs" />
                    <Skeleton w={272} h={9} rounded="xs" />
                  </div>
                  <div className="flex justify-end">
                    <Skeleton w={200} h={26} rounded="sm" />
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Getting started

   Prose in a 760px measure — the one screen in the product that is neither a
   table nor a grid of cards. Without its own fallback it inherits the generic
   nine-column table, which is the exact defect the header of this file is about:
   ~300ms of a shape the route is never going to hold.
   ──────────────────────────────────────────────────────────────────────────── */

/** Header row, the welcome card, then the first two checklist rows. */
export function GettingStartedSkeleton() {
  return (
    <div
      className="h-full min-h-0 overflow-hidden"
      aria-busy="true"
      aria-label="Loading getting started"
    >
      <div className="flex h-[var(--h-topbar)] shrink-0 items-center gap-2 px-3">
        <Skeleton w={168} h={20} rounded="sm" />
        <span className="flex-1" />
        <Skeleton w={132} h={26} rounded="sm" />
      </div>

      <div className="px-3">
        <div className="mx-auto w-full max-w-[760px]" aria-hidden>
          <div className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-line bg-[var(--surface-2)] p-5">
            <Skeleton w={392} h={18} rounded="sm" />
            <Skeleton w="100%" h={11} rounded="xs" />
            <Skeleton w="92%" h={11} rounded="xs" />
            <Skeleton w="64%" h={11} rounded="xs" />
            <div className="mt-1 flex gap-2">
              <Skeleton w={132} h={30} rounded="sm" />
              <Skeleton w={148} h={30} rounded="sm" />
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-2">
            <Skeleton w={168} h={16} rounded="sm" />
            <Skeleton w={480} h={11} rounded="xs" />
            {Array.from({ length: 3 }, (_, row) => (
              <div
                key={row}
                className="mt-1 flex gap-3 rounded-[var(--radius-lg)] border border-line bg-[var(--surface-2)] p-4"
                style={{ opacity: 1 - row * 0.18 }}
              >
                <Skeleton w={24} h={24} rounded="full" />
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <Skeleton w={188} h={12} rounded="xs" />
                  <Skeleton w="88%" h={10} rounded="xs" />
                  <Skeleton w="52%" h={10} rounded="xs" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The whole settings surface: rail + pane, for the layout chunk itself. */
export function SettingsScreenSkeleton() {
  return (
    <div className="flex h-full min-h-0 bg-[var(--surface-1)]">
      <div
        aria-hidden
        className="flex w-[212px] shrink-0 flex-col gap-5 border-r border-line bg-[var(--surface-2)] px-2 py-3"
      >
        {[2, 4, 2].map((count, group) => (
          <div key={group} className="flex flex-col gap-0.5">
            <div className="px-2 pb-1.5">
              <Skeleton w={64} h={8} rounded="xs" />
            </div>
            {Array.from({ length: count }, (_, item) => (
              <div
                key={item}
                className="flex h-[var(--h-control)] items-center gap-2.5 px-2"
              >
                <Skeleton w={14} h={14} rounded="xs" />
                <Skeleton w={88} h={10} rounded="xs" />
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <SettingsPaneSkeleton />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Record detail
   ──────────────────────────────────────────────────────────────────────────── */

/** Contact / company detail: header band, then a two-column body. */
export function RecordDetailSkeleton() {
  return (
    <div className="flex h-full flex-col" aria-busy="true" aria-label="Loading record">
      <div className="flex h-[var(--h-topbar)] shrink-0 items-center gap-3 border-b border-line px-[var(--space-6)]">
        <Skeleton w={32} h={32} rounded="full" />
        <div className="flex flex-col gap-1.5">
          <Skeleton w={196} h={14} rounded="xs" />
          <Skeleton w={132} h={10} rounded="xs" />
        </div>
        <span className="flex-1" />
        <Skeleton w={88} h={26} rounded="sm" />
        <Skeleton w={26} h={26} rounded="sm" />
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_320px] gap-[var(--space-4)] overflow-hidden p-[var(--space-5)]">
        <div className="flex flex-col gap-[var(--space-3)]">
          {Array.from({ length: 6 }, (_, row) => (
            <div
              key={row}
              aria-hidden
              style={{ opacity: Math.max(0.25, 1 - row * 0.13) }}
              className="flex items-center gap-3 rounded-[var(--radius-md)] border border-line-subtle bg-surface-1 px-3 py-3"
            >
              <Skeleton w={20} h={20} rounded="full" />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <Skeleton w="46%" h={11} rounded="xs" />
                <Skeleton w="72%" h={9} rounded="xs" />
              </div>
              <Skeleton w={56} h={10} rounded="xs" />
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-[var(--space-3)]">
          {Array.from({ length: 3 }, (_, card) => (
            <div
              key={card}
              aria-hidden
              className="flex flex-col gap-2.5 rounded-[var(--radius-lg)] border border-line bg-surface-1 p-[var(--space-4)]"
            >
              <Skeleton w={96} h={10} rounded="xs" />
              <Skeleton w="100%" h={11} rounded="xs" />
              <Skeleton w="64%" h={11} rounded="xs" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Tasks
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * `/tasks`, on the geometry the real screen settles into — literally: the track
 * list, the row pitch and the two sticky heights all come from the same
 * `taskGrid()` / `TASK_ROW_HEIGHT` / `TASK_HEAD_H` / `TASK_BAND_H` in
 * `lib/constants` that `TaskRow` and `TaskListHeader` render on. There used to
 * be a second, stale copy of the track list here; it was 136px light on fixed
 * track and 4px out on the gutter, so every cell right of the title jumped
 * ~150px sideways the moment the chunk resolved.
 *
 * Cell shapes mirror the real ones, not just the tracks: a square select box and
 * a round complete toggle in the two leading slots, a disc + name for the owner,
 * a dot + label for the stage, a glyph + P-code for the priority.
 *
 * `chrome={false}` drops the toolbar and the composer row — the screen itself
 * paints those before its data arrives, so only the list below them is unknown.
 * The same component is the screen's *in-place* loading state, which is why it
 * takes the live `columns` and `density`: two skeletons that disagreed with each
 * other was the second half of the same defect. `head` lets the screen pass the
 * real column head, whose labels and sort state are known before any request
 * resolves; the route fallback, which cannot import a feature module, falls back
 * to bars on the same tracks at the same height.
 */
/**
 * The Task cell's two bars, as a share of the cell rather than in px.
 *
 * The live titles measure 285–349px in a 512px cell at 1440 — 56–68% of it —
 * and the subject, where it renders at all, takes its 168px floor and a little
 * more. A share cannot outrun the track it is drawn in at any viewport, which
 * is the whole point: a skeleton row that fills a cell the data leaves a third
 * empty is a layout jump with extra steps.
 */
const TITLE_SHARE = ['58%', '66%', '54%', '68%', '61%', '56%', '64%', '60%'];
/** A share of what the title leaves — the live subject truncates and fills its box. */
const SUBJECT_SHARE = ['92%', '74%', '86%', '68%'];

export function TasksScreenSkeleton({
  chrome = true,
  bands = [8, 5],
  columns = TASK_COLUMNS_ALL,
  density = 'comfortable',
  head,
}: {
  chrome?: boolean;
  bands?: number[];
  columns?: TaskColumnVisibility;
  density?: Density;
  head?: ReactNode;
}) {
  const grid = taskGrid(columns);
  const list = (
    <>
      {head ?? (
        <div
          aria-hidden
          className="grid items-center gap-3 border-b border-line px-3"
          style={{ height: TASK_HEAD_H, gridTemplateColumns: grid }}
        >
          <span />
          <span />
          {columns.inquiry ? <Skeleton w={44} h={8} rounded="xs" /> : null}
          <Skeleton w={30} h={8} rounded="xs" />
          {columns.stage ? <Skeleton w={34} h={8} rounded="xs" /> : null}
          {columns.owner ? <Skeleton w={36} h={8} rounded="xs" /> : null}
          <Skeleton w={24} h={8} rounded="xs" className="justify-self-end" />
          {columns.priority ? <Skeleton w={44} h={8} rounded="xs" /> : null}
          <span />
        </div>
      )}

      {bands.map((rows, band) => (
        <div key={band}>
          <div
            aria-hidden
            /* The band's own chrome, not just its height: `--surface-2` and the
               top hairline that lands on the row rhythm, exactly as
               `TaskGroupHeader` draws them. */
            className="relative grid items-center gap-3 bg-[var(--surface-2)] px-3 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-[var(--line)] before:content-['']"
            style={{ height: TASK_BAND_H, gridTemplateColumns: TASK_BAND_GRID }}
          >
            <Skeleton w={10} h={10} rounded="xs" className="justify-self-center" />
            <div className="flex items-center gap-2">
              <Skeleton w={64} h={9} rounded="xs" />
              <Skeleton w={18} h={9} rounded="xs" />
              <Skeleton w={112} h={9} rounded="xs" />
            </div>
          </div>

          {Array.from({ length: rows }, (_, row) => (
            <div
              key={row}
              aria-hidden
              className="grid items-center gap-3 border-b border-line px-3"
              style={{ height: TASK_ROW_HEIGHT[density], gridTemplateColumns: grid }}
            >
              <Skeleton w={16} h={16} rounded="xs" className="justify-self-center" />
              <Skeleton w={16} h={16} rounded="full" className="justify-self-center" />
              {columns.inquiry ? <Skeleton w={52} h={10} rounded="xs" /> : null}
              {/* The Task cell, on the row's own rule.
                  It used to draw two fixed px bars — 248+12+160 on some rows,
                  i.e. edge to edge of a 420px cell — into a cell the data then
                  left 20–40% empty, because below `TASK_SUBJECT.visible` of
                  track the live row draws the title alone. So: the same
                  container query the row uses, the second bar suppressed under
                  the same threshold, and both bars sized as a *share* of the
                  cell, which is what makes them impossible to overflow at any
                  viewport or column configuration. */}
              <div className={cn(TASK_SUBJECT.container, 'flex min-w-0 items-center gap-3')}>
                <Skeleton w={TITLE_SHARE[row % TITLE_SHARE.length]} h={10} rounded="xs" />
                {/* `flex-1`, exactly as `TaskRow` gives the subject: the bar is
                    then a share of what the title leaves, and the pair can
                    never add up to more than the cell. */}
                <span className={cn(TASK_SUBJECT.visible, 'flex-1')}>
                  <Skeleton w={SUBJECT_SHARE[row % SUBJECT_SHARE.length]} h={10} rounded="xs" />
                </span>
              </div>
              {columns.stage ? (
                /* The dot is spent on terminal stages only — 8 of 15 rows on the
                   live list — so it cannot be on 100% of the skeleton's. */
                <div className="flex items-center gap-1.5">
                  <Skeleton w={10} h={10} rounded="full" />
                  <Skeleton w={40 + ((row * 7) % 12)} h={10} rounded="xs" />
                </div>
              ) : null}
              {columns.owner ? (
                <div className="flex items-center gap-1.5">
                  {/* The Avatar's own metric — the loading disc, the empty seat
                      and the filled one are one circle. */}
                  <Skeleton w={AVATAR_PX.sm} h={AVATAR_PX.sm} rounded="full" />
                  <Skeleton w={72} h={10} rounded="xs" />
                </div>
              ) : null}
              <Skeleton w={62} h={10} rounded="xs" className="justify-self-end" />
              {columns.priority ? (
                <div className="flex items-center gap-1">
                  <Skeleton w={14} h={14} rounded="xs" />
                  <Skeleton w={40} h={10} rounded="xs" />
                </div>
              ) : null}
              <span />
            </div>
          ))}
        </div>
      ))}
    </>
  );

  if (!chrome) return <div aria-busy="true" aria-label="Loading tasks">{list}</div>;

  return (
    <div
      className="flex h-full flex-col bg-[var(--surface-1)]"
      aria-busy="true"
      aria-label="Loading tasks"
    >
      {/* PageHeader — `--h-topbar`, `px-3`, title then description. */}
      <div className="flex h-[var(--h-topbar)] shrink-0 items-center gap-2 px-3">
        <Skeleton w={62} h={16} rounded="xs" />
        <Skeleton w={252} h={10} rounded="xs" />
      </div>

      {/* ListToolbar — 40px, `px-3`, `gap-1.5`, the same seven controls. */}
      <div className="flex h-10 shrink-0 items-center gap-1.5 border-b border-line-subtle px-3">
        <Skeleton w={236} h={30} rounded="sm" />
        <Skeleton w={76} h={30} rounded="sm" />
        <Skeleton w={150} h={30} rounded="sm" />
        <Skeleton w={148} h={30} rounded="sm" />
        <Skeleton w={96} h={30} rounded="sm" />
        <Skeleton w={58} h={30} rounded="sm" />
        <span className="flex-1" />
        <Skeleton w={92} h={12} rounded="xs" />
        <Skeleton w={84} h={30} rounded="sm" />
        <Skeleton w={104} h={30} rounded="sm" />
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">{list}</div>
    </div>
  );
}
